import { LoaderCircle, ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiClient, type LoginFailurePage } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { periodoDaConsulta } from "@/shared/utils/instant-period";

/**
 * Tentativas de login malsucedidas (2026-10-23, proposta de IAM §8.2 e
 * RN-19), dentro da Auditoria do Máster.
 *
 * Elas acontecem no Keycloak; a API as copia de lá a cada 5 minutos pra uma
 * tabela que nada apaga, e é dela que esta lista lê. O período é o da
 * página; os outros filtros (seção, empresa, quem) não se aplicam.
 */

/** Os códigos do Keycloak em português. O que não estiver aqui aparece
 *  como veio — melhor um código do que uma tradução errada. */
const MOTIVO: Record<string, string> = {
  invalid_user_credentials: "Senha ou código do autenticador errado",
  user_not_found: "E-mail não cadastrado",
  user_temporarily_disabled: "Conta bloqueada por tentativas erradas",
  user_disabled: "Conta desativada no Keycloak",
  access_denied: "Sem acesso ao ReputAItion",
  identity_provider_error: "Falha no login pelo Google",
  identity_provider_login_failure: "Falha no login pelo Google",
  invalid_authenticator: "Chave de acesso não reconhecida"
};

export function motivoDaFalha(codigo: string): string {
  return MOTIVO[codigo] ?? codigo;
}

export function LoginFailuresCard({ token, de, ate }: { token: string; de: string; ate: string }) {
  const [pagina, setPagina] = useState<LoginFailurePage | null>(null);
  const [numero, setNumero] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [tentativa, setTentativa] = useState(0);
  // Mesmo descarte por carga da lista de mudanças: só a consulta mais
  // recente escreve.
  const cargaAtual = useRef(0);

  // Trocou o período: volta pra primeira página.
  useEffect(() => setNumero(1), [de, ate]);

  useEffect(() => {
    const carga = ++cargaAtual.current;
    setCarregando(true);
    const periodo = periodoDaConsulta(de, ate);
    apiClient
      .listLoginFailures(token, {
        occurred_from: periodo.changed_from,
        occurred_to: periodo.changed_to,
        page: numero,
        size: 20
      })
      .then(resultado => {
        if (carga !== cargaAtual.current) return;
        setPagina(resultado);
        setErro(null);
      })
      .catch(error => {
        if (carga !== cargaAtual.current) return;
        setErro(errorMessage(error, "Não foi possível carregar as tentativas de login."));
      })
      .finally(() => {
        if (carga === cargaAtual.current) setCarregando(false);
      });
  }, [token, de, ate, numero, tentativa]);

  return (
    <div className="card">
      <div className="card-title">
        <div>
          <ShieldAlert size={18} aria-hidden="true" />
          <h3>Tentativas de login malsucedidas</h3>
          <p>
            {pagina
              ? `${pagina.total} tentativa${pagina.total === 1 ? "" : "s"} · copiadas do Keycloak a cada 5 minutos`
              : "Carregando…"}
          </p>
        </div>
        {carregando && <LoaderCircle className="spin" size={16} />}
      </div>

      {erro ? (
        <ApiState kind="error" onRetry={() => setTentativa(n => n + 1)}>
          <span role="alert">{erro}</span>
        </ApiState>
      ) : pagina && pagina.items.length === 0 ? (
        <p className="copiloto-empty-list">Nenhuma tentativa malsucedida nesse período.</p>
      ) : (
        <table className="ai-admin-table audit-table">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Quem</th>
              <th>IP</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {pagina?.items.map((falha, indice) => {
              const quando = parseServerTime(falha.occurred_at);
              return (
                <tr key={`${falha.occurred_at}-${indice}`}>
                  <td>{quando === null ? falha.occurred_at : new Date(quando).toLocaleString("pt-BR")}</td>
                  <td title={falha.person_name ? (falha.username ?? undefined) : undefined}>
                    {falha.person_name ?? falha.username ?? "—"}
                  </td>
                  <td>{falha.ip_address ?? "—"}</td>
                  <td>{motivoDaFalha(falha.error)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pagina && !erro && (
        <Pager page={pagina.page} size={pagina.size} count={{ total: pagina.total }} onPage={setNumero} />
      )}
    </div>
  );
}
